/**
 * Generated by orval v8.20.0.
 * Do not edit manually.
 * Hub API v2.0.0.
 */

import type { HttpContext, HttpEvent, HttpParams } from '@angular/common/http';
import { HttpResponse as AngularHttpResponse, HttpClient, HttpHeaders } from '@angular/common/http';

import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import type { AgencyAgentsList200, AgencyAgentsListParams, AgencySession200 } from '../../models';

interface HttpClientOptions {
  readonly headers?: HttpHeaders | Record<string, string | string[]>;
  readonly context?: HttpContext;
  readonly params?:
    | HttpParams
    | Record<string, string | number | boolean | Array<string | number | boolean>>;
  readonly reportProgress?: boolean;
  readonly withCredentials?: boolean;
  readonly credentials?: RequestCredentials;
  readonly keepalive?: boolean;
  readonly priority?: RequestPriority;
  readonly cache?: RequestCache;
  readonly mode?: RequestMode;
  readonly redirect?: RequestRedirect;
  readonly referrer?: string;
  readonly integrity?: string;
  readonly referrerPolicy?: ReferrerPolicy;
  readonly transferCache?: { includeHeaders?: string[] } | boolean;
  readonly timeout?: number;
}

type HttpClientBodyOptions = HttpClientOptions & {
  readonly observe?: 'body';
};

type HttpClientEventOptions = HttpClientOptions & {
  readonly observe: 'events';
};

type HttpClientResponseOptions = HttpClientOptions & {
  readonly observe: 'response';
};

type HttpClientObserveOptions = HttpClientOptions & {
  readonly observe?: 'body' | 'events' | 'response';
};

type AngularHttpParamValue = string | number | boolean | Array<string | number | boolean>;
type AngularHttpParamValueWithNullable = AngularHttpParamValue | null;

function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys?: ReadonlySet<string>,
  preserveRequiredNullables?: false,
  passthroughKeys?: undefined
): Record<string, AngularHttpParamValue>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: true,
  passthroughKeys?: undefined
): Record<string, AngularHttpParamValueWithNullable>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> | undefined,
  preserveRequiredNullables: boolean | undefined,
  passthroughKeys: ReadonlySet<string>
): Record<string, unknown>;
function filterParams(
  params: Record<string, unknown>,
  requiredNullableKeys: ReadonlySet<string> = new Set(),
  preserveRequiredNullables = false,
  passthroughKeys: ReadonlySet<string> = new Set()
): Record<string, unknown> {
  const filteredParams: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (passthroughKeys.has(key)) {
      if (value !== undefined) {
        filteredParams[key] = value;
      }
      continue;
    }
    if (Array.isArray(value)) {
      const filtered = value.filter(
        item =>
          item != null &&
          (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
      ) as Array<string | number | boolean>;
      if (filtered.length) {
        filteredParams[key] = filtered;
      }
    } else if (preserveRequiredNullables && value === null && requiredNullableKeys.has(key)) {
      filteredParams[key] = null;
    } else if (
      value != null &&
      (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    ) {
      filteredParams[key] = value;
    }
  }
  return filteredParams;
}

@Injectable({ providedIn: 'root' })
export class AgentsService {
  private readonly http = inject(HttpClient);
  /**
   * Returns a paginated list of the memberships of the session's agency, ordered by email. Every membership is returned, active or not, each carrying its own `active` flag, and `self` marks the logged-in agent's own row. Optional email, role and active filters narrow the results; the dashboard's managers card asks for `?role=manager&active=true`. Managers of the agency only, sub-agency managers included.<br><br>Replaces `GET /v1.5/agent/access/account/index` of the legacy agent API: an envelope with pagination metadata rather than a bare list, and `status` (`active`/`inactive`) is the boolean `active`.
   * @summary List agents
   */
  agencyAgentsList<TData = AgencyAgentsList200>(
    params?: AgencyAgentsListParams,
    options?: HttpClientBodyOptions
  ): Observable<TData>;
  agencyAgentsList<TData = AgencyAgentsList200>(
    params?: AgencyAgentsListParams,
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencyAgentsList<TData = AgencyAgentsList200>(
    params?: AgencyAgentsListParams,
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencyAgentsList<TData = AgencyAgentsList200>(
    params?: AgencyAgentsListParams,
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    const filteredParams = filterParams({ ...params, ...options?.params }, new Set<string>([]));

    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/agents`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
        params: filteredParams,
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/agents`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
        params: filteredParams,
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency/agents`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
      params: filteredParams,
    });
  }
  /**
   * Returns the logged-in agent's session for the requested agency, or for their default agency when the `ag-agency` header is omitted: the agent's role, the agency, and the consumer that owns it with its subscription. Any agent of the agency may read it, whatever their role, so the portal can tell a manager from everyone else. Each call records the agent's sign-in.<br><br>Replaces `GET /v1.5/agent/account/info` of the legacy agent API: `agency_id`, `agency`, `agency_image_url`, `agency_status` and `is_superagency` are `agency.id`, `agency.name`, `agency.logo_url`, `agency.active` and `agency.superagency`; `consumer_code`, `consumer`, `currency_code`, `profiles_type` and `umbrella_client_id` move under `consumer`; `plan` is `consumer.subscription.name`; `agent_id` is `external_agent_id`; `is_superuser` and `is_consumer_owner` are `superuser` and `consumer_owner`; `is_subagencies_available` is `consumer.subscription.subagencies_enabled`, the subscription's own flag; `token`, `rbac` and `umbrella_token` are gone.
   * @summary Agent session
   */
  agencySession<TData = AgencySession200>(options?: HttpClientBodyOptions): Observable<TData>;
  agencySession<TData = AgencySession200>(
    options?: HttpClientEventOptions
  ): Observable<HttpEvent<TData>>;
  agencySession<TData = AgencySession200>(
    options?: HttpClientResponseOptions
  ): Observable<AngularHttpResponse<TData>>;
  agencySession<TData = AgencySession200>(
    options?: HttpClientObserveOptions
  ): Observable<TData | HttpEvent<TData> | AngularHttpResponse<TData>> {
    if (options?.observe === 'events') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/session`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'events',
      });
    }

    if (options?.observe === 'response') {
      return this.http.get<TData>(`${environment.hubApiUrl}/agency/session`, {
        ...(options as Omit<NonNullable<typeof options>, 'observe'>),
        observe: 'response',
      });
    }

    return this.http.get<TData>(`${environment.hubApiUrl}/agency/session`, {
      ...(options as Omit<NonNullable<typeof options>, 'observe'>),
      observe: 'body',
    });
  }
}
